import React, { useState, lazy, Suspense } from 'react';
import { ToastsContext, Theme, seismicTheme } from '@seismic/mantle';
import useColors from 'ui/hooks/useColors';
import supportsLocalStorage from 'utils/supportsLocalStorage';
import { getService, TenantService } from '@seismic/browser-services';
import tenantEvents from 'utils/tenantEvents';
import { Spinner } from 'ui';

const TenantInfoCheck = new Promise(resolve => {
  //If the tenant info was initialized before this module was loaded
  if (supportsLocalStorage) {
    const tenantInfo = JSON.parse(localStorage.getItem('seismicTenantInfo'));
    if (tenantInfo) {
      resolve(tenantInfo);
    }
  }

  //If this module is loaded before the tenant info is initialized
  tenantEvents.on('tenantServiceInitd', tenantInfo => {
    resolve(tenantInfo);
  });
});

const UniversalUploader = lazy(() =>
  TenantInfoCheck.then(tenantInfo => {
    getService(TenantService).initTenantInfoByCache(tenantInfo);
    return import(
      /* webpackChunkName: "UUC2" */ '@seismic/universal-upload-component'
    )
      .then(module => ({ default: module.UniversalUploader }))
      .catch(console.error);
  }).catch(console.error),
);

const UniversalUploaderDnd = lazy(() =>
  TenantInfoCheck.then(tenantInfo => {
    getService(TenantService).initTenantInfoByCache(tenantInfo);
    return import(
      /* webpackChunkName: "UUCDnd" */ '@seismic/universal-upload-component'
    )
      .then(module => ({ default: module.UniversalUploaderDnd }))
      .catch(console.error);
  }).catch(console.error),
);

const UniversalUploaderButton = lazy(() =>
  TenantInfoCheck.then(tenantInfo => {
    getService(TenantService).initTenantInfoByCache(tenantInfo);
    return import(
      /* webpackChunkName: "UUCBtn" */ '@seismic/universal-upload-component'
    )
      .then(module => ({ default: module.UniversalUploaderButton }))
      .catch(console.error);
  }).catch(console.error),
);

const UniversalUploaderComponent = ({
  accept,
  onComplete,
  uploadMaxItems = 1,
  uploadBtnText,
}) => {
  const { colorPrimary } = useColors();
  const [uploads, setUploads] = useState([]);

  const handleComplete = newUploads => {
    setUploads(newUploads);
    let data;
    if (newUploads[0]?.storageService === 'bss') {
      data = {
        name: newUploads[0].file?.name,
        size: newUploads[0].file?.size,
        storageId: newUploads[0].storageId,
        containerId: newUploads[0].containerId,
        isSeismicAudio: true,
      };
    } else {
      data = {
        token: newUploads[0].storageId,
        isSeismic: true,
        format: newUploads[0].file?.name?.split('.').pop(),
      };
    }
    if (typeof onComplete === 'function') {
      onComplete(data);
    }
  };

  const bssUploadOptions = {
    container: 'readiness',
    owner: 'srs-lessons',
    isFullContainerName: false,
  };

  return (
    <Theme
      theme={seismicTheme({
        useGlobal: true,
        customization: {
          colors: {
            primary: colorPrimary,
          },
        },
      })}
      className="EditorElement-uploadButton d-f jc-c"
    >
      <ToastsContext>
        <Suspense fallback={<Spinner color="darker" size="16" />}>
          <UniversalUploader
            uploadMaxItems={uploadMaxItems}
            accept={accept}
            onComplete={handleComplete}
            bssUploadOptions={bssUploadOptions}
          >
            <UniversalUploaderDnd>
              <div className="mt-10 mb-24">
                <UniversalUploaderButton>
                  <span className="EditorElement-uploadButton--universalVideoUploader">
                    {uploadBtnText}
                  </span>
                </UniversalUploaderButton>
              </div>
            </UniversalUploaderDnd>
          </UniversalUploader>
          {uploads.length > 0 && <div className="d-f jc-c" />}
        </Suspense>
      </ToastsContext>
    </Theme>
  );
};

export default UniversalUploaderComponent;
